import { useCallback, useEffect, useRef, useState } from 'react';
import { CalendarDays, Check, Clock3, Info, MessageSquareText, Share2 } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { WebsiteRenderer } from '@agency/website-renderer';
import { useAgencyData } from '../data.js';
import { AgencyBrand } from '../components/AgencyBrand.js';
import { ChangeRequestModal } from '../components/ActionModal.js';
import { ProposalDetailsSheet } from '../components/ProposalDetailsSheet.js';
import { buildImportedPreviewUrl, formatPreviewAddress, formatProposalReference, formatProspectPrice, formatProspectStatus, getProposalPricing } from '../proposal-display.js';
import { ProposalAnalyticsRuntime, useProposalAnalytics } from '../analytics.js';

const companionPhoneQuery = '(min-width: 1024px)';
const primaryPhoneQuery = '(max-width: 1023px)';

function useMediaQuery(queryText: string) {
  const [enabled, setEnabled] = useState(() => typeof window !== 'undefined' && window.matchMedia(queryText).matches);

  useEffect(() => {
    const query = window.matchMedia(queryText);
    const update = () => setEnabled(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, [queryText]);

  return enabled;
}

function ProposalCountdown({ expiresAt, now, promotionActive }: { expiresAt: number; now: number; promotionActive: boolean }) {
  const remaining = Math.max(0, expiresAt - now);
  const days = Math.floor(remaining / 86_400_000);
  const hours = Math.floor((remaining % 86_400_000) / 3_600_000);
  const minutes = Math.floor((remaining % 3_600_000) / 60_000);
  const seconds = Math.floor((remaining % 60_000) / 1000);
  const compact = remaining > 0
    ? `${days}d ${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`
    : 'Expired';

  return <span className={`proposal-countdown${remaining > 0 ? '' : ' is-expired'}`} role="timer" aria-label={`Proposal ${remaining > 0 ? `expires in ${days} days, ${hours} hours, ${minutes} minutes, and ${seconds} seconds` : 'has expired'}`}>
    <Clock3 aria-hidden="true" />
    <span className="proposal-countdown-copy">
      <small>{remaining > 0 ? (promotionActive?'Promotion ends in':'Offer closes in') : 'Offer status'}</small>
      <time dateTime={new Date(expiresAt).toISOString()}>{compact}</time>
    </span>
  </span>;
}

export function ProposalPage() {
  const { slug } = useParams();
  const { getProspect, loading, refreshProspect, track } = useAgencyData();
  const prospect = getProspect(slug);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [changesOpen, setChangesOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [checkoutLoading,setCheckoutLoading]=useState(false);
  const [checkoutNotice,setCheckoutNotice]=useState('');
  const [now, setNow] = useState(Date.now());
  const showCompanionPhone = useMediaQuery(companionPhoneQuery);
  const primaryPhonePreview = useMediaQuery(primaryPhoneQuery);
  const desktopScrollRef = useRef<HTMLDivElement>(null);
  const phoneScrollRef = useRef<HTMLDivElement>(null);
  const desktopFrameRef = useRef<HTMLIFrameElement>(null);
  const phoneFrameRef = useRef<HTMLIFrameElement>(null);
  const iframeSyncLeaderRef = useRef<Window | null>(null);
  const iframeSyncReleaseRef = useRef<number | undefined>(undefined);
  const iframeTourLeaderRef = useRef<Window | null>(null);
  const iframeTourReleaseRef = useRef<number | undefined>(undefined);
  const scrollMilestonesRef = useRef<Record<'desktop'|'phone',Set<number>>>({desktop:new Set(),phone:new Set()});
  const tourPlayingRef = useRef<Record<'desktop'|'phone',boolean>>({desktop:false,phone:false});
  const lastTourEventRef = useRef<{name:string;at:number}>({name:'',at:0});

  useProposalAnalytics(slug,prospect?.rendererType);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(()=>{
    if(!slug)return;
    const params=new URLSearchParams(window.location.search);
    const outcome=params.get('checkout');
    if(outcome==='cancelled')setCheckoutNotice('Checkout canceled. Your proposal is still available.');
    if(outcome==='success'){
      const sessionId=params.get('session_id');
      setCheckoutNotice('Checkout completed. Confirming your payment…');
      if(sessionId)void fetch(`/api/prospects/${encodeURIComponent(slug)}/payment-status?session_id=${encodeURIComponent(sessionId)}`)
        .then(async response=>({ok:response.ok,body:await response.json() as {status?:string}}))
        .then(async({ok,body})=>{
          if(ok&&body.status==='paid'){
            await refreshProspect(slug);
            setCheckoutNotice('Payment confirmed. Thank you—Auri79 will follow up shortly.');
            const cleanUrl=new URL(window.location.href);cleanUrl.searchParams.delete('checkout');cleanUrl.searchParams.delete('session_id');window.history.replaceState({},'',cleanUrl);
          }else setCheckoutNotice('Checkout completed. Stripe is confirming your payment.');
        })
        .catch(()=>setCheckoutNotice('Checkout completed. Stripe is confirming your payment.'));
    }
  },[refreshProspect,slug]);

  useEffect(() => {
    const desktop = desktopScrollRef.current;
    const phone = phoneScrollRef.current;
    if (!desktop || !phone) return;

    let activeSource: HTMLElement | null = null;
    let releaseFrame = 0;
    const sync = (source: HTMLElement, target: HTMLElement) => {
      if (activeSource && activeSource !== source) return;
      const sourceRange = source.scrollHeight - source.clientHeight;
      const targetRange = target.scrollHeight - target.clientHeight;
      const progress = sourceRange > 0 ? source.scrollTop / sourceRange : 0;
      const nextTop = progress * Math.max(0, targetRange);
      if (Math.abs(target.scrollTop - nextTop) < 1) return;

      activeSource = source;
      target.scrollTop = nextTop;
      window.cancelAnimationFrame(releaseFrame);
      releaseFrame = window.requestAnimationFrame(() => { activeSource = null; });
    };
    const syncFromDesktop = () => sync(desktop, phone);
    const syncFromPhone = () => sync(phone, desktop);

    desktop.addEventListener('scroll', syncFromDesktop, { passive: true });
    phone.addEventListener('scroll', syncFromPhone, { passive: true });
    syncFromDesktop();
    return () => {
      desktop.removeEventListener('scroll', syncFromDesktop);
      phone.removeEventListener('scroll', syncFromPhone);
      window.cancelAnimationFrame(releaseFrame);
    };
  }, [prospect?.slug, prospect?.rendererType, showCompanionPhone]);
  useEffect(() => {
    if (!prospect?.importedSite) return;
    const onMessage = (event: MessageEvent) => {
      const desktopWindow = desktopFrameRef.current?.contentWindow;
      const phoneWindow = phoneFrameRef.current?.contentWindow;
      if (event.source !== desktopWindow && event.source !== phoneWindow) return;
      const viewport = event.source === phoneWindow || (primaryPhonePreview && event.source === desktopWindow) ? 'phone' : 'desktop';
      if (event.data?.source === 'prospect-preview' && event.data.type === 'scroll-progress') {
        const source = event.source as Window;
        if (iframeTourLeaderRef.current) return;
        const progress=Math.max(0,Math.min(1,Number(event.data.progress)||0));
        for(const milestone of [25,50,75,100])if(progress*100>=milestone&&!scrollMilestonesRef.current[viewport].has(milestone)){
          scrollMilestonesRef.current[viewport].add(milestone);
          track(prospect.slug,'scroll_depth',{viewport,scrollPercent:milestone});
        }
        const leader = iframeSyncLeaderRef.current;
        if (leader && leader !== source) return;
        iframeSyncLeaderRef.current = source;
        const target = event.source === desktopWindow ? phoneWindow : desktopWindow;
        target?.postMessage({ source: 'proposal-shell', type: 'set-scroll-progress', progress }, '*');
        window.clearTimeout(iframeSyncReleaseRef.current);
        iframeSyncReleaseRef.current = window.setTimeout(() => { iframeSyncLeaderRef.current = null; }, 650);
      } else if(event.data?.source==='prospect-preview'&&event.data.type==='interaction'){
        const metadata=event.data.metadata&&typeof event.data.metadata==='object'?event.data.metadata:{};
        track(prospect.slug,'preview_interaction',{viewport,element:String(metadata.element??'unknown').slice(0,40),label:String(metadata.label??'').slice(0,120)});
      } else if(event.data?.source==='prospect-preview'&&event.data.type==='ready'){
        track(prospect.slug,'preview_ready',{viewport});
      } else if (event.data?.source === 'prospect-scroll-world' && event.data.type === 'tour-state') {
        const source = event.source as Window;
        const playing=Boolean(event.data.playing);
        const progress=Math.max(0,Math.min(1,Number(event.data.progress)||0));
        // A mirrored companion reports the state it was just given. It must never
        // take leadership from the iframe that started the tour, otherwise both
        // previews start driving each other and repeatedly rebase their clocks.
        const currentLeader = iframeTourLeaderRef.current;
        if (currentLeader && currentLeader !== source) return;
        if(tourPlayingRef.current[viewport]!==playing){
          tourPlayingRef.current[viewport]=playing;
          const name=playing?'tour_started':progress>=0.98?'tour_completed':'tour_paused';
          const now=Date.now();
          if(lastTourEventRef.current.name!==name||now-lastTourEventRef.current.at>1_000){
            lastTourEventRef.current={name,at:now};
            track(prospect.slug,name,{viewport,scrollPercent:Math.round(progress*100)});
          }
        }
        window.clearTimeout(iframeTourReleaseRef.current);
        if (playing) {
          iframeTourLeaderRef.current = source;
        } else if (currentLeader === source) {
          iframeTourReleaseRef.current = window.setTimeout(() => { iframeTourLeaderRef.current = null; }, 1_500);
        }
        const target = event.source === desktopWindow ? phoneWindow : desktopWindow;
        target?.postMessage({
          source: 'proposal-shell',
          type: 'set-scroll-world-tour',
          playing,
          progress,
        }, '*');
      }
    };
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
      window.clearTimeout(iframeSyncReleaseRef.current);
      window.clearTimeout(iframeTourReleaseRef.current);
      iframeSyncLeaderRef.current = null;
      iframeTourLeaderRef.current = null;
    };
  }, [primaryPhonePreview,prospect?.importedSite,prospect?.slug,track]);
  const share = useCallback(async () => {
    if (!prospect) return;
    track(prospect.slug, 'share_clicked');
    const data = { title: `${prospect.businessName} website proposal`, text: 'Your custom website is ready to review.', url: location.href };
    try { if (navigator.share) await navigator.share(data); else { await navigator.clipboard.writeText(location.href); setCopied(true); window.setTimeout(() => setCopied(false), 1800); } } catch { /* dismissed */ }
  }, [prospect, track]);
  const schedule = () => {
    if (!prospect) return;
    track(prospect.slug, 'schedule_clicked');
    setDetailsOpen(false);
    window.open(`/api/prospects/${encodeURIComponent(prospect.slug)}/schedule`, '_blank', 'noopener,noreferrer');
  };
  const requestChanges = () => { if (prospect) track(prospect.slug, 'request_changes_clicked'); setDetailsOpen(false); setChangesOpen(true); };

  const startCheckout=useCallback(async()=>{
    if(!prospect||checkoutLoading||prospect.status==='purchased')return;
    track(prospect.slug,'claim_clicked');
    setCheckoutLoading(true);
    setCheckoutNotice('');
    try{
      const response=await fetch(`/api/prospects/${encodeURIComponent(prospect.slug)}/checkout`,{method:'POST',headers:{'content-type':'application/json'}});
      const body=await response.json() as {url?:string;error?:string};
      if(!response.ok||!body.url)throw new Error(body.error??'Secure checkout is unavailable.');
      window.location.assign(body.url);
    }catch(error){
      setCheckoutLoading(false);
      setCheckoutNotice(error instanceof Error?error.message:'Secure checkout is unavailable.');
    }
  },[checkoutLoading,prospect,track]);

  useEffect(()=>{
    if(!prospect||prospect.status==='purchased')return;
    const url=new URL(window.location.href);
    if(url.searchParams.get('checkout')!=='start')return;
    url.searchParams.delete('checkout');
    window.history.replaceState({},'',url);
    void startCheckout();
  },[prospect,startCheckout]);

  if (loading) return <main className="empty-state"><h1>Loading proposal…</h1></main>;
  if (!prospect) return <main className="empty-state"><h1>Proposal not found</h1><p>Check the link or contact Auri79 for a fresh preview.</p></main>;
  const pricing = getProposalPricing(prospect.createdAt,prospect.priceCents,prospect.promotionPriceCents,now);
  const price = formatProspectPrice(pricing.displayPriceCents);
  const fullPrice = formatProspectPrice(prospect.priceCents);
  const savings = formatProspectPrice(pricing.savingsCents);
  const status = formatProspectStatus(prospect.status);
  const proposalReference = formatProposalReference(prospect.id, prospect.slug);
  const previewAddress = formatPreviewAddress(window.location.host, prospect.slug);
  const included = prospect.importedSite ? ['Finished custom website', 'Interactive desktop preview', 'Mobile optimized', '1 year hosting included'] : ['Custom 5-page website', 'Mobile optimized', 'Local SEO foundation', '1 year hosting included'];
  const paymentNote = `One-time payment · ${included.at(-1) ?? 'Hosting included'}`;
  const importedUrl = prospect.importedSite?.url;
  const desktopImportedUrl=importedUrl?buildImportedPreviewUrl(importedUrl,'desktop'):null;
  const phoneImportedUrl=importedUrl?buildImportedPreviewUrl(importedUrl,'phone'):null;
  const primaryImportedUrl=primaryPhonePreview?phoneImportedUrl:desktopImportedUrl;
  const importedFramePermissions='autoplay';

  return <main className="proposal-shell">
    <ProposalAnalyticsRuntime />
    <aside className="proposal-sidebar">
      <div><AgencyBrand refreshCurrent /><p className="sidebar-label">Website preview for</p><h2>{prospect.businessName}</h2><div className="status-pill"><i /> {status}</div></div>
      <div className={`investment-card${pricing.promotionActive?' has-promotion':''}`}>
        <p>{pricing.promotionActive?'LIMITED-TIME INVESTMENT':'YOUR INVESTMENT'}</p>
        <div className="investment-price"><strong>{price}</strong>{pricing.promotionActive&&<del>{fullPrice}</del>}</div>
        <span>{pricing.promotionActive?`Save ${savings} before the offer closes`:'One-time payment'}</span>
      </div>
      <div className="included"><p>WHAT&apos;S INCLUDED</p>{included.map(item => <span key={item}><b>✓</b>{item}</span>)}</div>
      <div className="sidebar-actions">
        <button className="claim-button" type="button" aria-busy={checkoutLoading} disabled={checkoutLoading||prospect.status==='purchased'} onClick={()=>void startCheckout()}>{prospect.status==='purchased'?'Payment received':checkoutLoading?'Opening checkout…':'Claim This Website'} <span>→</span></button>
        <button className="schedule-button" type="button" onClick={schedule}><CalendarDays />Schedule A Call</button>
        <button className="change-link" type="button" onClick={requestChanges}><MessageSquareText />Request Website Changes</button>
      </div>
      <div className="proposal-meta"><span>Proposal #{proposalReference}</span><button type="button" onClick={share}><Share2 />Share</button></div>
    </aside>

    <section className="preview-stage">
      <div className="proposal-banner"><p><span>●</span>Your custom website is ready to review</p><div><ProposalCountdown expiresAt={pricing.expiresAt} now={now} promotionActive={pricing.promotionActive} /><button className="banner-checkout" type="button" aria-busy={checkoutLoading} disabled={checkoutLoading||prospect.status==='purchased'} onClick={()=>void startCheckout()}>{prospect.status==='purchased'?'Paid':checkoutLoading?'Opening…':`Claim for ${price}`}</button></div></div>
      <div className="browser-frame">
        <div className="browser-chrome"><div><i /><i /><i /></div><span>⌁ {previewAddress}</span><em>↻</em></div>
        <div className={`site-scroll${primaryImportedUrl ? ' imported-site-scroll' : ''}`} ref={desktopScrollRef}>{primaryImportedUrl ? <iframe ref={desktopFrameRef} className="imported-site-frame" src={primaryImportedUrl} title={`${prospect.businessName} ${primaryPhonePreview?'phone':'desktop'} website preview`} allow={importedFramePermissions} sandbox="allow-forms allow-modals allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts" /> : <WebsiteRenderer config={prospect.websiteConfig} />}</div>
      </div>
      {showCompanionPhone && <div className="mobile-device" aria-label="Mobile website preview">
        <div className="phone-scale"><div className={`phone-scroll${phoneImportedUrl ? ' imported-site-scroll' : ''}`} ref={phoneScrollRef}>{phoneImportedUrl ? <iframe ref={phoneFrameRef} className="imported-site-frame" src={phoneImportedUrl} title={`${prospect.businessName} phone website preview`} allow={importedFramePermissions} sandbox="allow-forms allow-modals allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts" /> : <WebsiteRenderer config={prospect.websiteConfig} compact />}</div></div>
      </div>}
    </section>

    <header className="mobile-proposal-bar">
      <div className="mobile-proposal-primary">
        <AgencyBrand compact refreshCurrent />
        <strong>Preview</strong>
        <ProposalCountdown expiresAt={pricing.expiresAt} now={now} promotionActive={pricing.promotionActive} />
        <button type="button" aria-label="View proposal information" onClick={() => {track(prospect.slug,'info_opened',{viewport:'phone'});setDetailsOpen(true);}}><Info /><span>Info</span></button>
        <button className="mobile-buy" type="button" aria-busy={checkoutLoading} disabled={checkoutLoading||prospect.status==='purchased'} onClick={()=>void startCheckout()}>{prospect.status==='purchased'?'Paid':checkoutLoading?'…':'Buy Now'}</button>
      </div>
    </header>
    <nav className="mobile-cta" aria-label="Proposal actions">
      <button type="button" onClick={schedule}><CalendarDays />Schedule</button><button type="button" onClick={share}><Share2 />{copied ? 'Copied' : 'Share'}</button><button className="buy" type="button" aria-busy={checkoutLoading} disabled={checkoutLoading||prospect.status==='purchased'} onClick={()=>void startCheckout()}><Check />{prospect.status==='purchased'?'Paid':checkoutLoading?'Opening…':'Buy Website'}</button>
    </nav>
    {copied && <div className="toast" role="status">Proposal link copied</div>}
    {checkoutNotice&&<div className="toast checkout-toast" role="status">{checkoutNotice}<button type="button" onClick={()=>setCheckoutNotice('')} aria-label="Dismiss checkout message">×</button></div>}
    <ProposalDetailsSheet open={detailsOpen} prospect={prospect} displayPriceCents={pricing.displayPriceCents} promotionActive={pricing.promotionActive} paymentNote={paymentNote} onClose={() => setDetailsOpen(false)} onClaim={()=>void startCheckout()} checkoutLoading={checkoutLoading} onSchedule={schedule} onShare={share} onChanges={requestChanges} />
    <ChangeRequestModal open={changesOpen} onClose={() => setChangesOpen(false)} onSubmit={async request => {
      const response=await fetch('/api/change-requests',{method:'POST',headers:{'content-type':'application/json'},body:JSON.stringify({slug:prospect.slug,...request})});
      const body=await response.json().catch(()=>({})) as {error?:string};
      if(!response.ok)throw new Error(body.error??'Your request could not be sent. Please try again.');
      track(prospect.slug,'change_request_submitted',{messageLength:request.message.length});
    }} />
  </main>;
}
